/**************************************************/
/* Methods for GLS based on wasm code             */
/**************************************************/

import { cor } from 'mdatools/stat';
import { ismatrix, Matrix, Index  } from 'mdatools/arrays';
import { isArrCorrect, checkedIndexCopy, alloc, allocCopy, checkModelBuffer, wasmError }
   from './utils.js';


/**
 * Returns length of array with GLS results.
 *
 * @param {number} nrows number of rows in a dataset.
 * @param {number} nresp number of response variables.
 * @param {number} ncomp number of components in the model.
 * @param {number} npred number of predictors in the model.
 *
 * @returns {number} the length of the results array.
 */
export function gls_getreslength(nrows, nresp, ncomp, npred) {
   // Structure of GLS results:
   // 4 +                  // ny, nx, nr, ncomp
   // nr * ncomp +         // contributions (C)
   // nr * ny    +         // predicted values (Yp)
   // 3 * nr     +         // q, h, f distances
   // 4 * ny  +            // RMSE, R2, bias, slope
   // 1 +                  // explained variance for X
   // nx * nr;             // x-residuals

   return 4 +
      nrows * ncomp +           // C
      nrows * nresp +           // Yp
      nrows * 4 +               // q, h, f, r
      nresp * 4 +               // RMSE, R2, bias, slope
      1 +                       // R2X
      nrows * npred;            // x-residuals
}


/**
 * Returns length of array with GLS model.
 *
 * @param {number} npred number of predictors.
 * @param {number} nresp number of responses.
 * @param {number} ncomp number of components
 *
 * @returns {number} the length of the model array.
 */
export function gls_getmodellength(npred, nresp, ncomp) {
   // 6 +                  // nx, ny, ncomp, alpha, lambda, method values
   // ncomp * nx +         // pure spectra (S')
   // nx * ncomp +         // B1-coefficients (S(S'S)^-1), ncomp x nx
   // (ncomp + 1) * ny +   // B2-coefficients (either ny * [b0, b11,...b1comp] or [b01 b11 b02 b22 b0ny b1ny])
   // 8;                   // q0, Nq, h0, Nh, f0, Nf, ecrit, ocrit

   return 6  +             // nx, ny, ncomp, alpha, lambda, method
      npred * ncomp +      // S'
      npred * ncomp +      // B1 coefficients
      nresp * (ncomp + 1) +// B2 coefficients
      8;                   // q0, Nq, h0, Nh, f0, Nf, ecrit, ocrit
}



/**
 * Returns part of GLS result array for given outcome.
 *
 * @param {TypedArray} r array with GLS results.
 * @param {string} name name of the outcome (e.g. 'Yp').
 * @param {number} a component number (not used, kept for compatibility).
 *
 * @returns {TypedArray} array with outcomes (by reference).
 */
export function glsres_get(r, name, a, ny) {

   if (!r || !r.class || !r.class.includes("glsres")) {
      throw new Error('glsres_get: parameter "r" must be JSON with GLS results.');
   }

   a = 1;

   if (!ny) {
      ny = 1;
   }

   let offset = 0;
   let length;
   const nrows = r.nrows;
   const ncomp = r.ncomp;
   const nresp = r.nresp;
   const npred = r.npred;
   const nresp_model = r.nresp_model;


   switch (name) {
      case 'T':
         offset = 4;
         length = ncomp * nrows;
         break;
      case 'Yp':
         offset = 4 + ncomp * nrows + (ny - 1) * nrows;
         length = nrows;
         break;
      case 'q':
         offset = 4 + ncomp * nrows + nresp_model * nrows;
         length = nrows;
         break;
      case 'h':
         offset = 4 + ncomp * nrows + nresp_model * nrows + nrows;
         length = nrows;
         break;
      case 'f':
         offset = 4 + ncomp * nrows + nresp_model * nrows + 2 * nrows;
         length = nrows;
         break;
      case 'r':
         offset = 4 + ncomp * nrows + nresp_model * nrows + 3 * nrows;
         length = nrows;
         break;
      case 'rmse':
         offset = 4 + ncomp * nrows + nresp_model * nrows + 4 * nrows;
         length = nresp_model;
         break;
      case 'r2':
         offset = 4 + ncomp * nrows + nresp_model * nrows + 4 * nrows + nresp_model;
         length = nresp_model;
         break;
      case 'bias':
         offset = 4 + ncomp * nrows + nresp_model * nrows + 4 * nrows + nresp_model * 2;
         length = nresp_model;
         break;
      case 'slope':
         offset = 4 + ncomp * nrows + nresp_model * nrows + 4 * nrows + nresp_model * 3;
         length = nresp_model;
         break;
      case 'r2x':
         offset = 4 + ncomp * nrows + nresp_model * nrows + 4 * nrows + nresp_model * 4;
         length = 1;
         break;
      case 'E':
         offset = 4 + ncomp * nrows + nresp_model * nrows + 4 * nrows + nresp_model * 4 + 1;
         length = nrows * npred;
         break;

      default:
         throw new Error('glsres_get: wrong outcome name.');
   }

   return r.v.subarray(offset, offset + length);
}


/**
 * Returns part of GLS model array for given outcome.
 *
 * @param {TypedArray} m array with GLS results.
 * @param {string} name name of the outcome (e.g. 'Yp').
 * @param {number} a component number (not used, kept for compatibility).
 *
 * @returns {TypedArray} array with outcomes (by reference).
 */
export function glsmodel_get(m, name, a, ny) {

   if (!m || !m.class || !m.class.includes("glsmodel")) {
      throw new Error('glsmodel_get: parameter "r" must be JSON with GLS results.');
   }

   const npred = m.npred;
   const nresp = m.nresp;
   const ncomp = m.ncomp;

   if (!ny) {
      ny = 1;
   }


   let offset = 0;
   let length;
   switch (name) {
      case 'P':
         // here instead of P we use S (D = CS') but S in model object
         // is already transposed, so we need to transpose it back
         offset = 6;
         length = ncomp * npred;
         const P = new Matrix(m.v.subarray(offset, offset + length), ncomp, npred);
         return P.t().v;
   }

   return null;
}

/**
 * Creates a GLS model implementation.
 *
 * @param {Object} Module object with wasm module for PCV.
 * @param {Matrix} D matrix with training set (predictors and responses).
 * @param {Matrix} St matrix with pure spectra.
 * @param {number} nresp number of responses.
 * @param {number} alpha regularization parameter for whitening.
 * @param {number} lambdap power for regularization parameter for Ridge regression (lambda = 10^lambdap,
 * or 0 for none when lambdap is 0 or undefined).
 * @param {Array} outliers array with indices of outliers.
 * @param {Array} exclvars array with indices of excluded variables.
 *
 * @returns {Object} JSON with GLS model.
 */
export function gls_fit(Module, D, St, nresp, alpha, lambdap, outliers, exclvars, method = 1) {

   const lambda = lambdap ? Math.pow(10, lambdap) : 0;

   if (!alpha) {
      alpha = 1
   }

   if (!ismatrix(D)) {
      throw new Error("gls_fit: D must be a matrix.");
   }

   if (!ismatrix(St)) {
      throw new Error("gls_fit: St must be a matrix.");
   }

   const ncomp = St.nrows;

   if (nresp < 1 || D.ncols < (2 + nresp)) {
      throw new Error("gls_fit: training set should have at least 2 predictors and 1 response.");
   }

   if (D.ncols - nresp !== St.ncols) {
      throw new Error("gls_fit: number of variables in training and pure sets should be the same.");
   }

   // process outliers
   if (!outliers) outliers = [];
   const nout = outliers && Array.isArray(outliers) ? outliers.length : 0;
   if (!isArrCorrect(outliers, 0, D.nrows - 1)) {
      throw new Error('gls_fit: some indices in "outliers" array are outside the expected range.');
   }
   // a fractional index truncates on the way to C, so the row C drops is not the row the caller
   // named while the caller's labels still count it - a silent disagreement. the copy is given
   // its own name rather than assigned back over the parameter: 'pca_fit' and 'pca_predict'
   // return the caller's own list in their result and 'test-pca-wasm-exclvars' pins that by
   // identity, so the same discipline is kept in every wrapper. taken after 'isArrCorrect' has
   // refused any non-array; see 'checkedIndexCopy' for why the copy rather than the array
   const outlierValues = checkedIndexCopy(outliers,
      'gls_fit: indices in "outliers" array must be whole numbers.');

   // process excluded variables
   if (!exclvars) exclvars = [];
   const nexv = exclvars && Array.isArray(exclvars) ? exclvars.length : 0;
   if (!isArrCorrect(exclvars, 0, D.ncols - nresp - 1)) {
      throw new Error('gls_fit: some indices in "exclvars" array are outside the expected range.');
   }

   // the same truncation as for the outlier indices above: a variable index that is not a whole
   // number reaches C through 'Int32Array' as a different column, so the model would be fitted
   // without a variable the caller never named. a separate name for the same reason as above -
   // the model returned below carries the caller's own 'exclvars' array
   const exclvarValues = checkedIndexCopy(exclvars,
      'gls_fit: indices in "exclvars" array must be whole numbers.');

   const nrows = D.nrows - nout;
   const npred = D.ncols - nresp - nexv;

   if (nrows < 3 || npred < 2) {
      throw new Error('gls_fit: D must have at least 3 rows and 2 predictors.');
   }

   // 'method' decides which of the component checks below applies, so an unknown value cannot be
   // left to fall through one of them - 'glsfit_wasm' refuses anything outside 1..2
   if (method !== 1 && method !== 2) {
      throw new Error('gls_fit: method must be 1 or 2 (got ' + method + ').');
   }

   // 'ncomp' arrives as the row count of the pure spectra block, so nothing above screens it
   // against the data. the three checks below mirror the refusals in 'glsfit_wasm' so a mismatch
   // names the dimension that is wrong instead of arriving as a generic failure. checked after
   // pruning because 'npred' has to be the count left once the excluded variables are removed
   if (ncomp < 1 || ncomp > npred) {
      throw new Error('gls_fit: the number of pure components must be between 1 and the number of predictors.');
   }

   // method 1 folds component 'nresp + 1' into 'nresp' and then fits each response on its own
   // column of C, so 'glsfit_wasm' refuses any other count. C compares against
   // the raw response count, which pruning never changes. the gls app cannot reach this - its
   // 'DataLoader' already forces 'nresp' to 'St.nrows' or 'St.nrows - 1' - but this wrapper is the
   // API that outlives the app and other callers carry no such constraint
   if (method === 1 && (ncomp < nresp || ncomp > nresp + 1)) {
      throw new Error('gls_fit: method 1 needs the number of pure components to be ' + nresp +
         ' or ' + (nresp + 1) + ' (got ' + ncomp + ').');
   }

   // the row bound belongs to method 2 alone: it regresses on the augmented contribution matrix,
   // which is 'nrows x (ncomp + 1)', and 'glsfit_wasm' refuses the QR of a wide one.
   // method 1 needs no such bound
   if (method === 2 && ncomp > nrows - 1) {
      throw new Error('gls_fit: method 2 needs at least ' + (ncomp + 1) + ' rows for ' + ncomp + ' components.');
   }

   // 'allocCopy' takes the element width from the heap view rather than from the array, so the
   // block size and the write offset can not disagree. for data that is an invariant guard rather
   // than a live fix: 'matrix()' coerces its values to Float64Array, so no saved file can put a
   // narrower array in 'D.v' or 'St.v'. it is only reachable from in-repo code, because 'ismatrix'
   // is 'X.constructor === Matrix' and a Matrix checks its values only in the constructor while
   // '.v' stays writable. the reachable width problem is on the MODEL buffer, in 'gls_predict'.
   // what matters here is the rest: 'alloc' refuses a byte count of 2^32 or more, which
   // '_malloc' would otherwise coerce to a small positive at the i32 boundary. that is reachable
   // with under 1 MB of input, because the length formula multiplies 'nresp' by 'ncomp + 1' while
   // the data only pays for it once - see 'docs/wasm/model-buffer-validation.md'
   const m_length = gls_getmodellength(npred, nresp, ncomp);
   const m_constructor = Float64Array;
   let d_ptr = 0, st_ptr = 0, o_ptr = 0, ev_ptr = 0, m_ptr = 0;
   let model;

   try {

      // allocate memory in WASM heap space and copy data values
      d_ptr = allocCopy(Module, 'HEAPF64', D.v, 'data');

      // allocate memory in WASM heap space and copy pure spectra values
      st_ptr = allocCopy(Module, 'HEAPF64', St.v, 'pure spectra');

      // allocate memory in WASM heap space and copy outliers values
      if (nout > 0) {
         o_ptr = allocCopy(Module, 'HEAP32', new Int32Array(outlierValues), 'outliers');
      }

      // allocate memory in WASM heap space and copy excluded variables values
      if (nexv > 0) {
         ev_ptr = allocCopy(Module, 'HEAP32', new Int32Array(exclvarValues), 'excluded variables');
      }

      // allocate memory in WASM heap space for model and calibration set results outcomes
      m_ptr = alloc(Module, m_length * m_constructor.BYTES_PER_ELEMENT, 'model');

      // call WASM function for GLS
      const mark = Module.errlines?.length ?? 0;
      const res = Module.ccall(
         'glsfit_wasm',
         [ 'number'], // return type
         [ 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number'],
         [d_ptr, D.nrows, D.ncols - nresp, nresp, st_ptr, ncomp, m_ptr, o_ptr, nout, ev_ptr, nexv, method, alpha, lambda],
      );

      if (res !== 1) {
         throw wasmError(Module, 'gls_fit', mark);
      }

      // copy part of HEAP memory with model outcomes. this has to stay a 'slice' - it makes an
      // independent buffer, while a 'subarray' would be a view into memory the 'finally' frees
      model = Module.HEAPF64.slice(
         m_ptr / m_constructor.BYTES_PER_ELEMENT,
         m_ptr / m_constructor.BYTES_PER_ELEMENT + m_length,
      );

   } finally {

      // the frees used to be written out twice, once on the error path and once on the success
      // path, so anything throwing in between leaked every block - a trapping 'ccall' is the
      // reachable one. '_free(0)' is a no-op, so the pointers need no guard
      Module._free(d_ptr);
      Module._free(st_ptr);
      Module._free(m_ptr);
      Module._free(o_ptr);
      Module._free(ev_ptr);
   }

   // locate beginning of B1 coefficients in the model vector
   let offset = 6 + (ncomp * npred);
   const coeffs = model.slice(offset, offset + ncomp * npred);


   // locate beginning of distance parameters in the model vector
   offset = 6 + (ncomp * npred) * 2 + nresp * (ncomp + 1);
   const q0 = model[offset];
   const Nq = model[offset + 1];
   const h0 = model[offset + 2];
   const Nh = model[offset + 3];
   const f0 = model[offset + 4];
   const Nf = model[offset + 5];
   const ecrit = model[offset + 6];
   const ocrit = model[offset + 7];

   // return GLS model object
   return {
      class: ['glsmodel'],
      v: model,
      ncomp,
      nrows,
      nresp,
      npred,
      alpha,
      lambda,
      exclvars,
      coeffs,
      h0, Nh, q0, Nq, f0, Nf, ecrit, ocrit
   };
}


/**
 * Projects dataset to a PLS model.
 *
 * @param {Object} Module object with wasm module for PCV.
 * @param {Object} m JSON with GLS model created by `gls_fit()`.
 * @param {Matrix} D matrix with dataset.
 * @param {Number} nresp number of responses in D.
 * @param {Array} outliers array with indices of outliers.
 *
 * @returns {Object} JSON with PLS results.
 */
export function gls_predict(Module, m, D, nresp, outliers) {

   if (!m || !m.class || !m.class.includes('glsmodel')) {
      throw new Error('gls_predict: parameter "m" must be JSON with GLS model.');
   }

   // only the dimensional prefix is checked - the GLS header continues with 'alpha', 'lambda' and
   // 'method', and a default 'lambda' is 0 while a non default one is a power of ten. the header
   // order is [nresp, npred, ncomp] and 'gls_getmodellength' takes (npred, nresp, ncomp)
   checkModelBuffer(m, 'gls_predict', [[0, 'nresp'], [1, 'npred'], [2, 'ncomp']],
      (ny, nx, ncomp) => gls_getmodellength(nx, ny, ncomp));

   if (!ismatrix(D)) {
      throw new Error('gls_predict: D must be a matrix.');
   }

   // process outliers
   if (!outliers) outliers = [];

   // the line above has already turned every falsy value into an empty list, as it always did, so
   // only a TRUTHY non-array can fail this check - the falsy ones reach it and pass as an empty
   // list. the type is checked BEFORE the copy is taken: 'Array.from' erases the container type -
   // a typed array or an array-like comes back as an ordinary array holding the same values - so a
   // check placed after it could no longer tell what the caller passed, and those would be
   // honoured as outliers where they used to be refused
   if (!Array.isArray(outliers)) {
      throw new Error('gls_predict: some indices in "outliers" array are outside the expected range.');
   }

   // read ONCE into a plain array and use only that copy below, its own length included. every
   // re-read of the caller's array is a chance to see a different value, and the row C removes
   // has to be the row the reference block keeps back
   const outlierValues = Array.from(outliers);
   const nout = outlierValues.length;

   if (!isArrCorrect(outlierValues, 0, D.nrows - 1)) {
      throw new Error('gls_predict: some indices in "outliers" array are outside the expected range.');
   }

   // 'isArrCorrect' bounds the values but does not require whole numbers, and a fractional index
   // truncates on the way to C. that row would then be missing from the results while the caller's
   // own labels and indices, built from the untruncated list, still count it - a silent
   // disagreement rather than a failure, so refuse it here. 'Number.isInteger' also refuses NaN,
   // which 'isArrCorrect' lets through because both of its comparisons are false, and entries
   // that are not numbers at all: 'true' used to reach C as row 1 and 'a' as row 0
   if (!outlierValues.every(Number.isInteger)) {
      throw new Error('gls_predict: indices in "outliers" array must be whole numbers.');
   }

   const outlierRows = nout > 0 ? new Int32Array(outlierValues) : null;

   // the model keeps the caller's 'exclvars' array by REFERENCE, and a model read from a file
   // brings its own, so these values have never been through the check in the fit function - the
   // caller can even mutate the array after fitting. the truncation is the same: a column index
   // that is not a whole number reaches C as a different column.
   //
   // the container is tested here because this path has no 'isArrCorrect' to do it. everything
   // computed from these indices reads the dense copy. that is naming discipline, not a second
   // line of defence: the guard above has already refused every list the copy would differ from,
   // and nothing here is async, so re-reading 'm.exclvars' would be harmless. the result hands
   // 'm.exclvars' itself back as metadata, unchanged. see 'checkedIndexCopy'
   if (!Array.isArray(m.exclvars)) {
      throw new Error('gls_predict: indices in "exclvars" taken from the model must be whole numbers.');
   }
   const modelExclvars = checkedIndexCopy(m.exclvars,
      'gls_predict: indices in "exclvars" taken from the model must be whole numbers.');

   // excluded variables are taken from the model
   let nexv = modelExclvars.length;
   let exclvars = modelExclvars;

   // compute numbers of rows and columns without excluded ones
   const nrows = D.nrows - nout;

   // check if dataset alread excluded variables the ecluded variables array will be emptied
   if ((D.ncols - nresp) === m.npred) {
      exclvars = [];
      nexv = 0;
   }

   // compute new number of predictors
   const npred = D.ncols - nexv - nresp;

   if (nrows < 1) {
      throw new Error('gls_predict: D must have at least 1 row.');
   }

   if (npred !== m.npred) {
      throw new Error('gls_predict: D must have the same number of predictors (without excluded ones) as the training set.');
   }


   // 'allocCopy' is what keeps the size and the offset in step here: it takes the element width
   // from the heap view, so no property of 'm.v' can make them disagree. that matters because
   // 'checkModelBuffer' pins the TYPE of 'm.v' and not its width - a class extending Float64Array
   // can report a narrower 'BYTES_PER_ELEMENT' and still pass 'instanceof'. 'alloc' refuses a byte
   // count of 2^32 or more, which '_malloc' would coerce to a small positive at the i32 boundary:
   // the result buffer reaches that with a 1 MB model and a 131 KB dataset
   const r_length = gls_getreslength(nrows, m.nresp, m.ncomp, m.npred);
   const r_constructor = Float64Array;
   let d_ptr = 0, o_ptr = 0, ev_ptr = 0, m_ptr = 0, r_ptr = 0;
   let res_values;

   try {

      // allocate memory in WASM heap space and copy dataset values
      d_ptr = allocCopy(Module, 'HEAPF64', D.v, 'data');

      // allocate memory in WASM heap space and copy outliers values
      if (nout > 0) {
         o_ptr = allocCopy(Module, 'HEAP32', outlierRows, 'outliers');
      }

      // allocate memory in WASM heap space and copy excluded variables values
      if (nexv > 0) {
         ev_ptr = allocCopy(Module, 'HEAP32', new Int32Array(modelExclvars), 'excluded variables');
      }

      // allocate memory in WASM heap space and copy model values
      m_ptr = allocCopy(Module, 'HEAPF64', m.v, 'model');

      // allocate memory in WASM heap space for result values
      r_ptr = alloc(Module, r_length * r_constructor.BYTES_PER_ELEMENT, 'results');

      // call WASM function for GLS prediction
      const mark = Module.errlines?.length ?? 0;
      const res = Module.ccall(
         'glspredict_wasm',
         ['number'], // return type
         ['number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number', 'number'], // argument types
         [d_ptr, D.nrows, D.ncols - nresp, nresp, m_ptr, r_ptr, o_ptr, nout, ev_ptr, nexv],
      ); // arguments

      if (res !== 1) {
         throw wasmError(Module, 'gls_predict', mark);
      }

      // copy part of HEAP memory with result values. this has to stay a 'slice' - it makes an
      // independent buffer, while a 'subarray' would be a view into memory the 'finally' frees
      res_values = Module.HEAPF64.slice(
         r_ptr / r_constructor.BYTES_PER_ELEMENT,
         r_ptr / r_constructor.BYTES_PER_ELEMENT + r_length,
      );

   } finally {

      // see the note in 'gls_fit' - the frees were written out twice and anything throwing
      // between the first allocation and them leaked every block
      Module._free(d_ptr);
      Module._free(m_ptr);
      Module._free(r_ptr);
      Module._free(o_ptr);
      Module._free(ev_ptr);
   }

   // return GLS results
   const resobj = {
      class: ['glsres'],
      v: res_values,
      ncomp: m.ncomp,
      nrows: nrows,
      npred: npred,
      nresp: nresp,
      nresp_model: m.nresp,
      outliers: outlierValues,
      exclvars: m.exclvars,
      modelName: m.fileName,
      modelHash: m.hash
   };

   const offset = 4 + m.ncomp * nrows;
   const Yp = new Matrix(res_values.subarray(offset, offset + nrows * m.nresp), nrows, m.nresp);
   resobj.Yp = Yp;

   if (nresp > 0) {
      // 'Yp' comes back from C with the outlier rows already removed, so the reference block must
      // lose the same rows or 'cor' below gets two vectors of different length and throws. C drops
      // them in 'prune_data' by walking the rows in order and skipping the flagged ones, so keeping
      // ascending order here is what pairs each prediction with its own reference. 'outliers' is
      // 0-based, 'subset' is 1-based
      let rind = null;
      if (nout > 0) {
         const dropped = new Set(outlierRows);
         rind = [];
         for (let i = 0; i < D.nrows; i++) {
            if (!dropped.has(i)) rind.push(i + 1);
         }
      }

      const Yref = D.subset(rind, Index.seq(1, m.nresp));
      resobj.Yref = Yref;

      const corr = new Float64Array(m.nresp);
      for (let i = 0; i < m.nresp; i++) {
         corr[i] = cor(Yp.getcolref(i+1), Yref.getcolref(i+1));
      }

      const rmse = new Matrix(glsres_get(resobj, "rmse"), 1, m.nresp);
      const r2 = new Matrix(glsres_get(resobj, "r2"), 1, m.nresp);
      const slope = new Matrix(glsres_get(resobj, "slope"), 1, m.nresp);
      const bias = new Matrix(glsres_get(resobj, "bias"), 1, m.nresp);
      const r2x = glsres_get(resobj, "r2x");

      // add statistics and return
      resobj.stat = {
         rmse: rmse,
         r2: r2,
         slope: slope,
         bias: bias,
         corr: new Matrix(corr, 1, m.nresp),
         r2x: r2x[0]
      };


   } else {
      resobj.stat = null;
   }

   return resobj;
}

